/**
 * A live recording waiting for Go (ROLES-AND-GO two-stage start): everything
 * is already recording, but the procedure hasn't been marked as started. In
 * the OR nobody may touch the phone, so a circulating nurse or the control
 * room can press Go here; the phone picks it up within a few seconds.
 */
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { extractErrorMessage } from '../api/errors';
import { requestLiveGo, type LiveSessionSummary } from '../api/live';

export function StandbyGoBanner({
  tenantId,
  session,
  canGo,
}: {
  tenantId: string;
  session: LiveSessionSummary;
  /** Operators and admins may press Go; viewers only see the state. */
  canGo: boolean;
}) {
  const [sent, setSent] = useState(Boolean(session.go_requested));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!session.standby) return null;
  const who = session.participant_label || 'A recording';
  const what = session.procedure_name ? ` · ${session.procedure_name}` : '';

  return (
    <Alert
      severity={error ? 'error' : 'warning'}
      action={
        canGo && !sent ? (
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await requestLiveGo(tenantId, session.session_id);
                setSent(true);
              } catch (e) {
                setError(extractErrorMessage(e, 'could not send Go'));
              } finally {
                setBusy(false);
              }
            }}
          >
            Go
          </Button>
        ) : undefined
      }
    >
      <strong>{who}</strong>
      {what} — {sent
        ? 'Go sent; the phone starts the procedure within a few seconds.'
        : 'recording on standby, waiting for Go.'}
      {error ? ` ${error}` : ''}
    </Alert>
  );
}
